import React, { useMemo } from "react";
import styles from "./HubFilters.module.css";
import { GUIDED_JOURNEY_TARGET } from "../../../config/guidedJourneyConfig";

const VISIBILITY_OPTIONS = [
  { key: "public", label: "Public" },
  { key: "private", label: "Private" },
];

const TYPE_OPTIONS = [
  { key: "product", label: "Product" },
  { key: "service", label: "Service" },
];

const StatusPill = React.memo(function StatusPill({ label, count, active, onClick, id }) {
  return (
    <button
      id={id}
      className={`${styles.pill} ${active ? styles.pillActive : ""}`}
      onClick={onClick}
      role="radio"
      aria-checked={active}
    >
      <span className={`${styles.pillDot} ${active ? styles.pillDotActive : ""}`} />
      {label}
      {count != null && <span className={styles.pillBadge}>{count}</span>}
    </button>
  );
});

const ToggleChip = React.memo(function ToggleChip({ label, active, onClick }) {
  return (
    <button
      className={`${styles.chip} ${active ? styles.chipActive : ""}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
});

const HubFilters = React.memo(function HubFilters({
  statusTab,
  onStatusChange,
  visibilityFilters,
  onVisibilityChange,
  typeFilters,
  onTypeFilterChange,
  shops,
}) {
  const isStoreLive = (shop) => {
    if (typeof shop?.BizSettings?.bizLive === "boolean") return shop.BizSettings.bizLive;
    return true;
  };

  const counts = useMemo(() => {
    let live = 0, draft = 0, trash = 0;
    shops.forEach((shop) => {
      const deleted = (shop.activeFlag || "").toString().toUpperCase() === "DELETED";
      if (deleted) { trash += 1; return; }
      if (!isStoreLive(shop)) { draft += 1; }
      else { live += 1; }
    });
    return { live, draft, trash };
  }, [shops]);

  const availableVisibility = useMemo(() => {
    const hasPublic = shops.some((s) => !s.bizPrivate && (s.activeFlag || "").toString().toUpperCase() !== "DELETED");
    const hasPrivate = shops.some((s) => s.bizPrivate && (s.activeFlag || "").toString().toUpperCase() !== "DELETED");
    return { public: hasPublic, private: hasPrivate };
  }, [shops]);

  const availableTypes = useMemo(() => {
    const hasProduct = shops.some((s) => (s.productSegment || s.storeType === "product") && (s.activeFlag || "").toString().toUpperCase() !== "DELETED");
    const hasService = shops.some((s) => (s.serviceSegment || s.storeType === "service") && (s.activeFlag || "").toString().toUpperCase() !== "DELETED");
    return { product: hasProduct, service: hasService };
  }, [shops]);

  const handleVisibilityToggle = (key) => {
    const next = visibilityFilters.includes(key)
      ? visibilityFilters.filter((c) => c !== key)
      : [...visibilityFilters, key];
    onVisibilityChange(next);
  };

  const handleTypeToggle = (key) => {
    const next = typeFilters.includes(key)
      ? typeFilters.filter((c) => c !== key)
      : [...typeFilters, key];
    onTypeFilterChange(next);
  };

  return (
    <div className={styles.filters}>
      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Status</span>
        <div className={styles.pillRow} role="radiogroup">
          <StatusPill label="Live" count={counts.live} active={statusTab === "live"} onClick={() => onStatusChange("live")} />
          <StatusPill
            id={GUIDED_JOURNEY_TARGET.DRAFT_TAB}
            label="Draft"
            count={counts.draft}
            active={statusTab === "draft"}
            onClick={() => onStatusChange("draft")}
          />
          <StatusPill label="Trash" count={counts.trash} active={statusTab === "trash"} onClick={() => onStatusChange("trash")} />
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Visibility</span>
        <div className={styles.chipRow}>
          {VISIBILITY_OPTIONS.map((opt) => (
            <ToggleChip
              key={opt.key}
              label={opt.label}
              active={visibilityFilters.includes(opt.key)}
              onClick={() => handleVisibilityToggle(opt.key)}
            />
          ))}
          {!availableVisibility.public && !availableVisibility.private ? (
            <span className={styles.chipHint}>No visibility data for current hub</span>
          ) : visibilityFilters.length === 0 ? (
            <span className={styles.chipHint}>Showing all</span>
          ) : null}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Business Type</span>
        <div className={styles.chipRow}>
          {TYPE_OPTIONS.map((opt) => (
            <ToggleChip
              key={opt.key}
              label={opt.label}
              active={typeFilters.includes(opt.key)}
              onClick={() => handleTypeToggle(opt.key)}
            />
          ))}
          {!availableTypes.product && !availableTypes.service ? (
            <span className={styles.chipHint}>No type data for current hub</span>
          ) : typeFilters.length === 0 ? (
            <span className={styles.chipHint}>Showing all</span>
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default HubFilters;
